import { defineStore } from 'pinia';
import type { MigrationDeployment, MigrationDeploymentAdd } from '@schema';
import api from '@/common/utils/api';
import useCommonStore, { type FetchOptions } from '@/common/store/pinia/common';
import mapper, { MappedDeployment } from '@/common/store/pinia/deployments/deploymentsMapper';
import { getDeploymentsUrl, getMigrationsUrl } from '@/common/store/pinia/maasUrls';
import CancellationError from '@/common/utils/errors/CancellationError';
import { abortByKey } from '@/common/utils/takeLastByKey';
import { takeLastByKeyIgnoreCancel } from '@/common/utils/takeLastByKeyIgnoreCancel';
import { DeploymentStatus } from '@/common/constants/deployment.constants';
import { useMigrationTasksStore } from '@/common/store/pinia/migrationTasks';
import { notifyDeploymentStatusChange } from '@/common/store/pinia/deployments/deploymentNotifications';
import { isNotificationForCurrentProject } from '@/common/utils/notifications/isNotificationForCurrentProject';

export const useDeploymentsStore = defineStore('deployments', () => {
  const migrationTasksStore = useMigrationTasksStore();

  const commonStore = useCommonStore<MigrationDeployment, MappedDeployment>({
    getUrl: getMigrationsUrl,
    itemKey: 'id',
    plural: (data) => (data as { data?: MigrationDeployment[] })?.data ?? [],
    mapItem: mapper,
    updateMethod: 'patch',
    dataChangeMode: 'direct'
  })();

  const {
    getItemById,
    getItemsByView,
    addViewList,
    setData,
    removeItem: removeItemBase,
    pushData
  } = commonStore;

  const removeItem = (id: ID) => {
    migrationTasksStore.removeTasksForParent(id as string);
    removeItemBase(id);
  };

  const fetchItem = (
    migrationId: string,
    deploymentId: string,
    options: FetchOptions = {}
  ) => {
    const view = `migration-${migrationId}`;
    return api.get<MigrationDeployment>(
      `${getDeploymentsUrl(migrationId)}/${deploymentId}`,
      undefined,
      { abortController: options.abortController }
    )
      .then((response) => {
        migrationTasksStore.setTasksForParent(response.data, 'deployment', migrationId);
        pushData([response.data], view);
      });
  };

  const fetchItems = (migrationId?: string, options: FetchOptions = {}) => {
    if (!migrationId) {
      return Promise.resolve({ data: [] as MappedDeployment[] });
    }
    const view = `migration-${migrationId}`;
    addViewList(view);
    return api.get<{ data: MigrationDeployment[] }>(
      getDeploymentsUrl(migrationId),
      undefined,
      { abortController: options.abortController }
    )
      .then((response) => {
        response.data.data.forEach(
          deployment => migrationTasksStore.setTasksForParent(deployment, 'deployment', migrationId)
        );
        setData(response.data.data, view);
      })
      .catch((error) => {
        if (error instanceof CancellationError) {
          throw error;
        }
        setData([], view);
      })
      .then(() => ({ data: getItemsByView.value(view) }));
  };

  const fetchItemsLatest = (migrationId: string) => takeLastByKeyIgnoreCancel(
    abortController => fetchItems(migrationId, { abortController }),
    getDeploymentsUrl(migrationId)
  );

  const cancelFetchItems = (migrationId: string) => {
    abortByKey(getDeploymentsUrl(migrationId));
  };

  const cancelDeployment = (migrationId: string, id: string) => api.post(`${getDeploymentsUrl(migrationId)}/${id}/cancel`)
    .then(() => fetchItems(migrationId));

  const recreateDeployment = (migrationId: string, deployment: MappedDeployment) => {
    const body: MigrationDeploymentAdd = {
      name: deployment.name,
      clone_disks: deployment.deploymentOpts.cloneDisks,
      force_deployment: deployment.deploymentOpts.forceDeployment,
      os_morphing: deployment.deploymentOpts.osMorphing,
      save_snapshot: deployment.deploymentOpts.saveSnapshot,
      start: deployment.deploymentOpts.start
    };
    return api.post(getDeploymentsUrl(migrationId), body)
      .then(() => fetchItems(migrationId));
  };

  const deleteDeployment = (migrationId: string, id: string) => api.delete(`${getDeploymentsUrl(migrationId)}/${id}`)
    .then(() => {
      removeItem(id);
    });

  const deleteItems = (migrationId: string, ids: string[]) => Promise.all(ids.map(id => deleteDeployment(migrationId, id)));

  const createDeployment = (migrationId: string) => {
    const view = `migration-${migrationId}`;
    const body: MigrationDeploymentAdd = {
      clone_disks: false,
      force_deployment: false,
      os_morphing: false,
      save_snapshot: false,
      start: false
    };
    return api.post<MigrationDeployment>(getDeploymentsUrl(migrationId), body)
      .then((response) => {
        migrationTasksStore.setTasksForParent(response.data, 'deployment', migrationId);
        pushData([response.data], view);
        return { data: getItemById.value(response.data?.id) };
      });
  };

  const fetchItemLatest = (migrationId: string, deploymentId: string) => takeLastByKeyIgnoreCancel(
    abortController => fetchItem(migrationId, deploymentId, { abortController }),
    `${getDeploymentsUrl(migrationId)}/${deploymentId}`
  );

  const onCompute = (data: SocketEvent<{
    id?: string;
    deployment_id?: string;
    migration_id?: string;
    project_id?: string;
    status?: DeploymentStatus;
    execution_id?: string;
  }>) => {
    if (!data?.object) return;
    if (!isNotificationForCurrentProject(data.object.project_id)) return;

    if (data.type === 'migration_tasks') {
      const taskId = data.object.id;
      // Known task: migrationTasks store already merged the change locally — no refetch needed.
      if (taskId && migrationTasksStore.getTaskById(taskId)) return;
      const executionId = data.object.execution_id;
      if (!executionId) return;
      const parent = getItemById.value(executionId);
      if (parent?.migrationId) fetchItemLatest(parent.migrationId, parent.id);
      return;
    }

    if (data.type !== 'migration_deployments') return;

    const { deployment_id: deploymentId, migration_id: migrationId, status } = data.object;
    if (!deploymentId) return;

    if (data.operation === 'deleted') {
      removeItem(deploymentId);
      return;
    }
    const existing = getItemById.value(deploymentId);
    const parentMigrationId = migrationId ?? existing?.migrationId;
    if (parentMigrationId) {
      fetchItemLatest(parentMigrationId, deploymentId);
    }

    const deploymentName = existing?.name ?? '';
    notifyDeploymentStatusChange(status, deploymentId, deploymentName);
  };

  return {
    ...commonStore,
    removeItem,
    fetchItem,
    fetchItems,
    fetchItemsLatest,
    cancelFetchItems,
    cancelDeployment,
    recreateDeployment,
    deleteDeployment,
    deleteItems,
    createDeployment,
    onCompute
  };
}, {
  ws: {
    compute: 'onCompute'
  }
});

export default useDeploymentsStore;
